/**
 * Catalog of base map (tile) layers available on the live map view.
 *
 * Each entry is a static raster tile source. The live map stacks every
 * enabled layer on top of each other (see `BaseLayers.tsx`), so opacity and
 * z-order — managed in `mapUiSlice` — let the operator blend e.g. satellite
 * imagery over OSM.
 *
 * MapTiler layers require an API key. It is read from the
 * `REACT_APP_MAPTILER_KEY` environment variable; a placeholder is used until
 * real credentials are provided. See `.env`.
 */

const MAPTILER_KEY = process.env.REACT_APP_MAPTILER_KEY || 'YOUR_MAPTILER_KEY';

const MAPTILER_ATTRIBUTION =
    '&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a> ' +
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

export interface BaseLayerDefinition {
    /** Stable identifier, used as the Redux key and React key. */
    id: string;
    /** Human-readable name shown in the layers panel. */
    label: string;
    /** FontAwesome (solid) icon name shown next to the label. */
    icon: string;
    /** Leaflet tile URL template. */
    url: string;
    /** Attribution HTML shown in the map's attribution control. */
    attribution: string;
    /** Maximum zoom level the source provides tiles for. */
    maxZoom: number;
    /** Whether this source needs a MapTiler key (used for a UI hint). */
    requiresKey?: boolean;
}

export const BASE_LAYERS: BaseLayerDefinition[] = [
    {
        id: 'osm',
        label: 'OpenStreetMap',
        icon: 'map',
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
    },
    {
        id: 'topo-v2',
        label: 'Topographic (MapTiler)',
        icon: 'mountain',
        url: `https://api.maptiler.com/maps/topo-v2/256/{z}/{x}/{y}.png?key=${MAPTILER_KEY}`,
        attribution: MAPTILER_ATTRIBUTION,
        maxZoom: 22,
        requiresKey: true,
    },
    {
        id: 'satellite',
        label: 'Satellite (MapTiler)',
        icon: 'satellite',
        url: `https://api.maptiler.com/maps/satellite/256/{z}/{x}/{y}.jpg?key=${MAPTILER_KEY}`,
        attribution: MAPTILER_ATTRIBUTION,
        maxZoom: 22,
        requiresKey: true,
    },
];

/** Look up a layer definition by id. */
export function getBaseLayer(id: string): BaseLayerDefinition | undefined {
    return BASE_LAYERS.find((layer) => layer.id === id);
}
